import { useState } from 'react'
import { Button } from '../../ui/button'
import { Checkbox } from '../../ui/checkbox'
import { Label } from '../../ui/label'
import { Textarea } from '../../ui/textarea'
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react'
import type { DealFormData } from '../../../lib/types'

interface StepAccessoryServicesProps {
  formData: DealFormData
  onComplete: (data: Partial<DealFormData>) => void
  onUpdate: (data: Partial<DealFormData>) => void
  onBack: () => void
  onSubmitDeal?: (data?: Partial<DealFormData>) => void
  isSubmitting?: boolean
  headingId?: string
  submitLabel?: string
  inProgressLabel?: string
  onValidationErrors?: (errors: { id: string; label: string }[]) => void
}

export function StepAccessoryServices({
  formData,
  onComplete,
  onUpdate,
  onBack,
  onSubmitDeal,
  isSubmitting = false,
  headingId,
  submitLabel = 'Submit',
  inProgressLabel = 'Submitting...',
  onValidationErrors,
  requestChanges,
}: StepAccessoryServicesProps & { requestChanges?: Record<string, string> }) {
  const [localData, setLocalData] = useState({
    accessoryServices: formData.accessoryServices || {
      marketing: false,
      kolCampaigns: false,
      marketMaking: false,
      treasuryManagement: false,
      otcDeals: false,
      auditing: false,
      hrRecruitment: false,
      cexListing: false,
      other: false,
      otherDescription: '',
    },
  })
  const [showLegalError, setShowLegalError] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const updateService = (service: string, value: boolean) => {
    const updatedServices: any = {
      ...localData.accessoryServices,
      [service]: value,
    }
    if (service === 'other' && !value) {
      updatedServices.otherDescription = ''
      setErrors((prev) => ({ ...prev, otherDescription: '' }))
    }
    const updatedData: any = { accessoryServices: updatedServices }
    setLocalData(updatedData)
    onUpdate(updatedData)
  }

  const updateOtherDescription = (description: string) => {
    const updatedServices: any = {
      ...localData.accessoryServices,
      otherDescription: description,
    }
    const updatedData: any = { accessoryServices: updatedServices }
    setLocalData(updatedData)
    onUpdate(updatedData)
    if (description.trim()) {
      setErrors((prev) => ({ ...prev, otherDescription: '' }))
    }
  }

  const handleSubmit = () => {
    const validationErrors: { id: string; label: string }[] = []

    if (
      localData.accessoryServices.other &&
      !localData.accessoryServices.otherDescription.trim()
    ) {
      setErrors((prev) => ({
        ...prev,
        otherDescription: 'Please describe your request',
      }))
      validationErrors.push({
        id: 'other-description',
        label: 'Other Service Description',
      })
    }

    if (!formData.marketingGuidelinesAgreementAccepted) {
      setShowLegalError(true)
      validationErrors.push({
        id: 'legal-agreements',
        label: 'Legal Agreements',
      })
    } else {
      setShowLegalError(false)
    }

    if (validationErrors.length > 0) {
      onValidationErrors?.(validationErrors)
      return
    }

    onComplete(localData)
    onValidationErrors?.([])
    onSubmitDeal?.(localData)
  }

  const services = [
    {
      key: 'marketing',
      label: 'Marketing / PR agencies',
      description:
        'Campaign planning, press outreach, content/asset creation, co‑marketing.',
    },
    {
      key: 'kolCampaigns',
      label: 'KOL campaigns (performance-based)',
      description:
        'Creator outreach with tracked referral links/codes. Tiered rewards and reporting.',
    },
    {
      key: 'marketMaking',
      label: 'Market making & liquidity (CEX & DEX)',
      description:
        'Order‑book support and spread targets on CEX; liquidity provisioning / pool setup on DEX.',
    },
    {
      key: 'treasuryManagement',
      label:
        'Treasury management (regulated managers for yield & monetization)',
      description:
        'Regulated managers (e.g., FINMA) for secure custody, yield generation, and token monetization.',
    },
    {
      key: 'otcDeals',
      label: 'OTC, LP & TVL deals',
      description:
        'Structured OTC placements, LP provisioning, and TVL bootstrapping with negotiated terms.',
    },
    {
      key: 'auditing',
      label: 'Auditing & security (discounted from top firms)',
      description:
        'Smart‑contract audits, security reviews, pen‑tests, and ongoing monitoring.',
    },
    {
      key: 'hrRecruitment',
      label: 'HR / Recruitment',
      description:
        'Sourcing for web3 roles, screening, shortlists, and offer support.',
    },
    {
      key: 'cexListing',
      label: 'CEX listing introductions',
      description:
        'Exchange intros, listing checklist, timeline coordination, and readiness review.',
    },
    {
      key: 'other',
      label: 'Other (please specify)',
      description: 'Request a service not listed here.',
    },
  ]

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        {requestChanges?.accessoryServices && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-status-error mt-0.5 mr-2" />
              <div>
                <h4 className="text-sm font-medium text-status-error">
                  Requested Changes
                </h4>
                <p className="text-xs text-status-error mt-1">
                  {requestChanges.accessoryServices}
                </p>
              </div>
            </div>
          </div>
        )}
        {services.map((service) => (
          <div key={service.key} className="space-y-3">
            <div className="flex items-start space-x-3">
              <Checkbox
                id={service.key}
                checked={(localData.accessoryServices as any)[service.key]}
                onCheckedChange={(checked) =>
                  updateService(service.key, checked === true)
                }
                className="mt-1 border-brand-primary rounded-sm text-brand-primary focus:ring-brand-primary accent-brand-primary"
              />
              <div className="flex-1">
                <Label
                  htmlFor={service.key}
                  className="text-sm font-medium text-neutral-900 cursor-pointer leading-relaxed block"
                >
                  {service.label}
                </Label>
                <p className="text-xs text-neutral-500 mt-1 leading-relaxed">
                  {service.description}
                </p>
              </div>
            </div>
            {service.key === 'other' &&
              (localData.accessoryServices as any).other && (
                <div className="ml-6 space-y-2">
                  <Label
                    htmlFor="other-description"
                    className="text-sm font-medium text-neutral-700"
                  >
                    Describe your request
                  </Label>
                  <div className="space-y-1">
                    <Textarea
                      id="other-description"
                      placeholder="e.g., localization, analytics, legal support"
                      value={
                        (localData.accessoryServices as any).otherDescription
                      }
                      onChange={(e) => updateOtherDescription(e.target.value)}
                      rows={3}
                      className={`max-w-md text-neutral-900 ${
                        errors.otherDescription ? 'border-status-error' : ''
                      }`}
                    />
                    <div className="text-xs text-neutral-400">
                      <span>
                        {!(
                          (localData.accessoryServices as any)
                            .otherDescription || ''
                        ).trim() &&
                          'Adding a short note helps our team route your request.'}
                      </span>
                    </div>
                    {errors.otherDescription && (
                      <p className="text-status-error text-xs mt-1 inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.otherDescription}
                      </p>
                    )}
                  </div>
                </div>
              )}
          </div>
        ))}
      </div>
      {showLegalError && (
        <div
          id="legal-agreements"
          className="p-4 bg-red-50 border border-red-200 rounded-lg"
        >
          <p className="text-xs text-red-600">
            Please review and accept the Legal Agreements to continue.
          </p>
        </div>
      )}
      <div className="flex justify-between pt-6 border-t border-neutral-200 border-none">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          size="md"
          className="flex items-center gap-2 bg-transparent px-5"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting}
          variant="brand"
          size="md"
          className="flex items-center gap-2 px-5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {inProgressLabel}
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </div>
  )
}
